import { apiClient } from "./client";
import type {
  SimulatorVersion,
  SimulatorVersionCreate,
  SimulatorVersionUpdate,
  SimulatorVersionsPaginated,
  SimulatorVersionAccess,
  SimulatorVersionAccessCreate,
  VersionStatus,
  MessageResponse,
  ExportedSimulatorVersion,
  VersionExportResponse,
} from "@/types";

interface ListVersionsParams {
  page?: number;
  page_size?: number;
  status?: VersionStatus;
}

export async function listVersions(
  simulatorId: number,
  params: ListVersionsParams = {}
): Promise<SimulatorVersionsPaginated> {
  const response = await apiClient.get<SimulatorVersionsPaginated>(
    `/simulators/${simulatorId}/versions`,
    { params }
  );
  return response.data;
}

export async function getVersion(versionId: number): Promise<SimulatorVersion> {
  const response = await apiClient.get<SimulatorVersion>(
    `/simulators/versions/${versionId}`
  );
  return response.data;
}

export async function createVersion(
  simulatorId: number,
  data: SimulatorVersionCreate
): Promise<SimulatorVersion> {
  const response = await apiClient.post<SimulatorVersion>(
    `/simulators/${simulatorId}/versions`,
    data
  );
  return response.data;
}

export async function updateVersion(
  versionId: number,
  data: SimulatorVersionUpdate
): Promise<SimulatorVersion> {
  const response = await apiClient.patch<SimulatorVersion>(
    `/simulators/versions/${versionId}`,
    data
  );
  return response.data;
}

export async function publishVersion(versionId: number): Promise<SimulatorVersion> {
  const response = await apiClient.post<SimulatorVersion>(
    `/simulators/versions/${versionId}/publish`
  );
  return response.data;
}

export async function archiveVersion(versionId: number): Promise<SimulatorVersion> {
  const response = await apiClient.post<SimulatorVersion>(
    `/simulators/versions/${versionId}/archive`
  );
  return response.data;
}

export async function unarchiveVersion(versionId: number): Promise<SimulatorVersion> {
  const response = await apiClient.post<SimulatorVersion>(
    `/simulators/versions/${versionId}/unarchive`
  );
  return response.data;
}

export async function deleteVersion(versionId: number): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}`
  );
  return response.data;
}

export async function duplicateVersion(versionId: number): Promise<SimulatorVersion> {
  const response = await apiClient.post<SimulatorVersion>(
    `/simulators/versions/${versionId}/duplicate`
  );
  return response.data;
}

export async function rollbackToVersion(versionId: number): Promise<SimulatorVersion> {
  const response = await apiClient.post<SimulatorVersion>(
    `/simulators/versions/${versionId}/rollback`
  );
  return response.data;
}

// Access management
export async function listVersionAccess(
  versionId: number
): Promise<SimulatorVersionAccess[]> {
  const response = await apiClient.get<SimulatorVersionAccess[]>(
    `/simulators/versions/${versionId}/access`
  );
  return response.data;
}

export async function createVersionAccess(
  versionId: number,
  data: SimulatorVersionAccessCreate
): Promise<SimulatorVersionAccess> {
  const response = await apiClient.post<SimulatorVersionAccess>(
    `/simulators/versions/${versionId}/access`,
    data
  );
  return response.data;
}

export async function deleteVersionAccess(
  versionId: number,
  accessId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}/access/${accessId}`
  );
  return response.data;
}

// Export (legacy endpoint)
export async function exportVersion(
  versionId: number,
  additionalTableIds?: number[],
  _includeTestScenarios?: boolean
): Promise<VersionExportResponse> {
  const params: Record<string, string> = {};
  if (additionalTableIds && additionalTableIds.length > 0) {
    params.additional_table_ids = additionalTableIds.join(",");
  }
  // Note: includeTestScenarios is not used by backend as test scenarios are always included
  // Use the newer export-version endpoint that returns VersionExportResponse format
  const response = await apiClient.get<VersionExportResponse>(
    `/export-version/${versionId}`,
    { params }
  );
  return response.data;
}

/**
 * Download the exported version as a JSON file
 */
export async function downloadVersionExport(versionId: number, filename?: string): Promise<void> {
  const data = await exportVersion(versionId);
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || `${data.simulator.slug}_v${data.version.version_number}_export.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

